import { useEffect, useState } from 'react';

/**
 * A CSS media query as a boolean, for the cases where hiding is not enough.
 *
 * `hidden md:block` is the right tool almost everywhere — it costs nothing
 * and needs no JavaScript. It is the wrong tool when the hidden branch is
 * expensive, because a display:none element is still MOUNTED: its effects
 * run, its queries fetch, its subscriptions fire.
 *
 * That is exactly what went wrong on the dispatcher view. The mobile map
 * sheet and the desktop map panel were both in the tree with Tailwind
 * breakpoints deciding which was visible, so both mounted Leaflet, both
 * pulled tiles, and both kept a marker set in sync with the driver stream —
 * on every screen size, permanently, with one of them invisible. The comment
 * in DispatcherView even claimed the breakpoint mounted only one. It did not.
 *
 * Use this only for that shape of problem. Reaching for JavaScript to do a
 * media query's job elsewhere trades a free, flicker-free CSS rule for a
 * render pass and a first paint at the wrong breakpoint.
 */
export function useMediaQuery(query) {
  // Server/first render has no window. Defaulting to false rather than true
  // means the cheap branch renders first and the expensive one waits for the
  // effect, which is the safer order if the answer arrives a frame late.
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    // Re-read on subscribe: the viewport can change between the initial
    // state above and this effect running.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Tailwind's `md` breakpoint, kept in one place so the JS and the CSS
 *  cannot drift apart. */
export const MD_BREAKPOINT_QUERY = '(min-width: 768px)';
